// frontend/src/routes.js
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import Layout from './components/Layout';
import ProtectedRoute from './ProtectedRoute';

// Opening Page
import WelcomePage from './pages/WelcomePage';

// Core Teacher Pages
import TeacherDashboard from './pages/TeacherDashboard';
import ManageStudents from './pages/ManageStudents';
import Reports from './pages/Reports';
import Store from './pages/Store';

// Award / Assign Points
import AwardPoints from './pages/AwardPoints';
import HabitSelectPage from './pages/AssignPoints/HabitSelectPage';
import ReasonSelectPage from './pages/AssignPoints/ReasonSelectPage';
import StudentSelectPage from './pages/AssignPoints/StudentSelectPage';
import RosterSelectPage from './pages/AssignPoints/RosterSelectPage';
import HouseSelectPage from './pages/AssignPoints/HouseSelectPage';
import RosterPointFlow from './pages/AssignPoints/RosterPointFlow';
import HousePointFlow from './pages/AssignPoints/HousePointFlow';

// Rosters
import RosterDashboard from './pages/Rosters/RosterDashboard';
import RosterCreatePage from './pages/Rosters/RosterCreatePage';
import RosterEditPage from './pages/Rosters/RosterEditPage';
import RosterViewPage from './pages/Rosters/RosterViewPage';

// Auth pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPasswordLanding from './pages/ForgotPasswordLanding';
import ForgotPasswordEducator from './pages/ForgotPasswordEducator';
import ForgotPasswordScholar from './pages/ForgotPasswordScholar';

// Student detail (educator-facing)
import StudentHistoryPage from './pages/StudentHistoryPage';

// Educator security setup
import EducatorSecuritySetup from './pages/EducatorSecuritySetup';

// Scholar self-view (NEW)
import MyPoints from './pages/MyPoints';

export default function AppRoutes() {
  return (
    <Routes>
      {/* Welcome / root */}
      <Route path="/" element={<WelcomePage />} />
      <Route path="/welcome" element={<WelcomePage />} />

      {/* Auth & account */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/recover" element={<ForgotPasswordLanding />} />
      <Route path="/recover/educator" element={<ForgotPasswordEducator />} />
      <Route path="/recover/scholar" element={<ForgotPasswordScholar />} />

      {/* Educator Security Setup (educator only, protected, no layout) */}
      <Route
        path="/educator/security/setup"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <EducatorSecuritySetup />
          </ProtectedRoute>
        }
      />

      {/* Shared dashboard for ALL authenticated users */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout><TeacherDashboard /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Scholar: My Points (scholar only) */}
      <Route
        path="/my-points"
        element={
          <ProtectedRoute allowedRoles={['SCHOLAR']}>
            <Layout><MyPoints /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Students (educator only) */}
      <Route
        path="/students"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><ManageStudents /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-students"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><ManageStudents /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Reports (educator only) */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><Reports /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Store (available to any authenticated user) */}
      <Route
        path="/store"
        element={
          <ProtectedRoute>
            <Layout><Store /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Award / Assign Points entry (educator only) */}
      <Route
        path="/award"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><AwardPoints /></Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/award-points" element={<Navigate to="/award" replace />} />

      {/* Assign Points flows (educator only) */}
      <Route
        path="/assign/students"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><StudentSelectPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assign/habits"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><HabitSelectPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assign/reasons"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><ReasonSelectPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assign/roster"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><RosterSelectPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assign/roster/points"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><RosterPointFlow /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assign/house"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><HouseSelectPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assign/house/points"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><HousePointFlow /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Rosters area (educator only) */}
      <Route
        path="/rosters"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><RosterDashboard /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rosters/new"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><RosterCreatePage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rosters/:id"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><RosterViewPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rosters/:id/edit"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><RosterEditPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Student detail (educator only) */}
      <Route
        path="/students/:id/history"
        element={
          <ProtectedRoute allowedRoles={['EDUCATOR']}>
            <Layout><StudentHistoryPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
